import React, { useState } from 'react';
import { useFormState } from '../useFormState';
import {
    Button,
    Card,
    CardActionArea,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Fade,
    Grid,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AddCircle,
    AutoAwesome,
    Close,
    Edit,
    Visibility,
} from '@mui/icons-material';
import AIDiscountsAssistant from '../../Fees/Discounts/AIDiscountAssistant';
import { ModalProps } from '../../../../types/modal';
import { useSelector } from 'react-redux';
import { selectExperienceDiscounts } from '../../../../../app/Slices/DiscountsSlice';
import { useGetDiscountsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import AIEditDiscounts from '../../Fees/Discounts/AIEditDiscounts';
import UserRole from '../../../../../shared/AuthRoles';

const AIModal = ({ open, onClose }: ModalProps) => {
    const { experience } = useFormState();

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>
                Discounts %
                <IconButton
                    onClick={onClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    sx={{
                        mb: 3,
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: '14px',
                    }}
                >
                    via 7DAY AI Assistant
                </DialogContentText>

                <AIDiscountsAssistant />
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={onClose}>
                    {experience.discounts.length === 0 ? 'Close' : 'Done'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const EditModal = ({ open, onClose }: ModalProps) => {
    const theme = useTheme();
    const loggedInUser = UserRole();
    const { experience } = useFormState();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const newExperienceDiscounts = useSelector(selectExperienceDiscounts);
    //get discounts applied to existing experience
    const { data: getDiscounts } = useGetDiscountsQuery(
        `/owners/${loggedInUser?.company}/discounts`,
    );

    const displayExperienceIdDiscounts =
        newExperienceDiscounts?.length !== 0
            ? newExperienceDiscounts
            : getDiscounts?.filter((i: any) => {
                  return i?.module?.id === experience.id;
              });

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>
                Your Discounts
                {experience.discounts.length !== 0
                    ? ''
                    : ` for ${experience.name}`}
                <IconButton
                    onClick={onClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: isMobile ? 'flex-start' : 'center',
                    }}
                >
                    <AIEditDiscounts discounts={experience.discounts} />
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={onClose}>
                    {displayExperienceIdDiscounts?.length === 0
                        ? 'Done'
                        : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ExperienceDiscounts = () => {
    const { experience } = useFormState();
    const { discounts } = experience;

    const [showAIModal, setShowAIModal] = useState(false);
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const handleOpenDiscountModal = () => {
        if (discounts.length === 0)
            setShowAIModal(true); // no discounts, show AI modal
        else setShowOptionsModal(true); // existing discounts, offer the option to edit or create a new one
    };

    const handleOpenNewDiscountModal = () => {
        //if there are no discounts show AI modal
        setShowAIModal(true);
        setShowOptionsModal(false);
    };

    const handleOpenEditDiscountModal = () => {
        //if there are  discounts show AI Edit modal
        setShowEditModal(true);
        setShowOptionsModal(false);
    };

    return (
        <Stack spacing={2}>
            <div>
                <Typography variant='h2'>
                    Discounts <AutoAwesome sx={{ color: 'primary.main' }} />
                </Typography>
                <Typography sx={{ color: 'text.secondary' }} gutterBottom>
                    Apply discounts to your experience to attract more
                    reservations.
                </Typography>
            </div>
            <div>
                <Card
                    onClick={() => {
                        handleOpenDiscountModal();
                    }}
                    sx={{
                        boxShadow: 1,
                        background: 'none',
                        height:
                            experience.type === 'commercial'
                                ? '270px'
                                : '230px',
                        backgroundColor: 'transparent',
                        color: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                            cursor: 'pointer',
                            color: 'neutral.main',
                            backgroundColor: 'transparent',
                            background: 'none',
                        },
                        '&:focus': {
                            cursor: 'pointer',
                            color: 'neutral.main',
                            backgroundColor: 'transparent',
                            background: 'none',
                        },
                    }}
                    style={{ flex: 1 }}
                >
                    <CardActionArea
                        sx={{
                            background: 'none',
                            backgroundColor: 'transparent',
                            color: 'neutral.main',
                            '&:hover': {
                                cursor: 'pointer',
                                color: 'neutral.main',
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                            '&:focus': {
                                cursor: 'pointer',
                                color: 'neutral.main',
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                    >
                        <Grid
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'neutral.main',
                            }}
                        >
                            <Grid>
                                <Typography
                                    gutterBottom
                                    component='div'
                                    sx={{
                                        fontSize: '14px',
                                        color: 'dark',
                                        '&:hover': {
                                            color: 'primary',
                                        },

                                        padding: '0px 5px 0px 0px',
                                        textAlign: 'right',
                                    }}
                                >
                                    <Edit
                                        fontSize='small'
                                        sx={{ fontSize: '16px' }}
                                    />
                                </Typography>
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '80px',
                                        paddingTop: 0,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: 'primary.main',
                                            // fontSize: isIpad ? '16px':''
                                        }}
                                    >
                                        Discounts
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: 'dark',
                                            fontSize: '12px',
                                            paddingLeft: '2px',
                                        }}
                                    >
                                        Apply discounts to your listed
                                        experience to attract more bookings.
                                    </Typography>
                                </CardContent>
                            </Grid>
                        </Grid>
                    </CardActionArea>
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px',
                        }}
                    >
                        <Grid
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {discounts.length > 0 && (
                                <>
                                    {' '}
                                    <Typography
                                        sx={{
                                            color: 'dark',
                                            fontSize: '14px',
                                        }}
                                    >
                                        You have {discounts.length} discounts
                                        for this experience.
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'dark',
                                            fontSize: '14px',
                                        }}
                                    >
                                        Active:{' '}
                                        {
                                            discounts.filter(
                                                (discounts) =>
                                                    discounts?.expired ===
                                                        false &&
                                                    discounts?.disabled ===
                                                        false,
                                            ).length
                                        }
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'dark',
                                            fontSize: '14px',
                                        }}
                                    >
                                        Disabled:{' '}
                                        {
                                            discounts?.filter(
                                                (discounts) =>
                                                    discounts?.disabled ===
                                                    true,
                                            ).length
                                        }
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'dark',
                                            fontSize: '14px',
                                        }}
                                    >
                                        Expired:{' '}
                                        {
                                            discounts?.filter(
                                                (discounts) =>
                                                    discounts?.expired === true,
                                            ).length
                                        }
                                    </Typography>
                                </>
                            )}

                            {discounts.map((discount: any, index: number) => (
                                <Fade in={true} key={index} timeout={500}>
                                    <Typography
                                        sx={{
                                            color: 'dark',
                                            fontSize: '14px',
                                        }}
                                    >
                                        {/* {' '}
                                    <li>
                                        {discount?.name
                                            .charAt(0)
                                            ?.toUpperCase() +
                                            discount?.name?.slice(
                                                1
                                            )}{' '}
                                        - {discount?.percentage}%
                                        discount for (
                                        {discount?.dates?.length ===
                                        1
                                            ? `${moment(
                                                  discount
                                                      ?.dates?.[0]
                                                      .startDate
                                              ).format(
                                                  'DD/MM/YYYY'
                                              )} - ${moment(
                                                  discount
                                                      ?.dates?.[0]
                                                      .endDate
                                              ).format(
                                                  'DD/MM/YYYY'
                                              )}`
                                            : discount?.dates?.map(
                                                  (i: any) => {
                                                      return `${moment(
                                                          i?.startDate
                                                      ).format(
                                                          'DD/MM/YYYY'
                                                      )}
                                                              -
                                                              ${moment(
                                                                  i?.endDate
                                                              ).format(
                                                                  'DD/MM/YYYY'
                                                              )},`;
                                                  }
                                              )}
                                        )
                                    </li>{' '} */}
                                    </Typography>
                                </Fade>
                            ))}
                            <Fade in={discounts.length === 0} timeout={500}>
                                <Typography
                                    sx={{
                                        color: 'dark',
                                        fontSize: '14px',
                                    }}
                                >
                                    No discounts have been added
                                </Typography>
                            </Fade>
                        </Grid>
                    </CardContent>
                </Card>
            </div>
            <AIModal open={showAIModal} onClose={() => setShowAIModal(false)} />
            <OptionsModal
                open={showOptionsModal}
                onClose={() => setShowOptionsModal(false)}
                handleNew={handleOpenNewDiscountModal}
                handleEdit={handleOpenEditDiscountModal}
            />
            <EditModal
                open={showEditModal}
                onClose={() => setShowEditModal(false)}
            />
        </Stack>
    );
};

const OptionsModal = ({
    open,
    onClose,
    handleNew,
    handleEdit,
}: { handleNew: () => void; handleEdit: () => void } & ModalProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Discounts
                <IconButton
                    onClick={onClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    sx={{
                        mb: 3,
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '14px',
                    }}
                >
                    Create a new discount or view your existing discounts.
                </DialogContentText>
                <Grid
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <Card
                        onClick={handleNew}
                        sx={{
                            width: isMobile ? 'auto' : 400,
                            minWidth: isMobile ? 150 : 200,
                            maxWidth: 300,
                            maxHeight: 100,
                            textAlign: 'center',
                            marginRight: isMobile ? '0px' : '10px',
                            marginBottom: isMobile ? '10px' : '0px',
                            boxShadow: 1,
                            background: 'none',
                            backgroundColor: 'transparent',
                            color: 'none',
                            '&:hover': {
                                cursor: 'pointer',
                                color: 'primary.main',
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                            '&:focus': {
                                cursor: 'pointer',
                                color: 'primary.main',
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                    >
                        <CardActionArea
                            // disableRipple
                            // disableTouchRipple
                            sx={{
                                background: 'none',
                                backgroundColor: 'transparent',
                                color: theme.palette.neutral.main,
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                                '&:focus': {
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                            }}
                        >
                            <CardContent
                                sx={{
                                    '&:hover': {
                                        color: 'primary.main',
                                    },
                                }}
                            >
                                <Typography
                                    gutterBottom
                                    variant='h5'
                                    component='div'
                                    sx={{
                                        color: 'primary.main',
                                    }}
                                >
                                    <AddCircle />
                                </Typography>
                                <Typography
                                    variant='h6'
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            color: 'primary',
                                        },
                                    }}
                                >
                                    {isMobile
                                        ? 'New %'
                                        : 'Create New Discount %'}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                    <Card
                        onClick={handleEdit}
                        sx={{
                            width: isMobile ? 'auto' : 400,
                            minWidth: isMobile ? 150 : 200,
                            maxWidth: 300,
                            maxHeight: 100,
                            textAlign: 'center',
                            marginRight: isMobile ? '0px' : '10px',
                            marginBottom: isMobile ? '10px' : '0px',
                            boxShadow: 1,
                            background: 'none',
                            backgroundColor: 'transparent',
                            color: 'none',
                            '&:hover': {
                                cursor: 'pointer',
                                color: theme.palette.neutral.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                            '&:focus': {
                                cursor: 'pointer',
                                color: theme.palette.neutral.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                    >
                        <CardActionArea
                            // disableRipple
                            // disableTouchRipple
                            sx={{
                                background: 'none',
                                backgroundColor: 'transparent',
                                color: theme.palette.neutral.main,
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: 'primary',
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                                '&:focus': {
                                    cursor: 'pointer',
                                    color: 'primary',
                                    backgroundColor: 'transparent',
                                    background: 'none',
                                },
                            }}
                        >
                            <CardContent>
                                <Typography
                                    gutterBottom
                                    variant='h5'
                                    component='div'
                                    sx={{
                                        color: 'primary.main',
                                    }}
                                >
                                    <Visibility />
                                </Typography>
                                <Typography
                                    variant='h6'
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            color: 'primary.main',
                                        },
                                    }}
                                >
                                    {isMobile
                                        ? 'View %'
                                        : ' View Your Discounts %'}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExperienceDiscounts;
